/**
 * ModifyUserTypePage
 *
 * Ported from: mobile change_user_type_screen.dart
 *
 * Shows a responsive card grid of all available user types (fan + admin excluded).
 * Multi-select: click a card to toggle selection.
 * On save, sends the selected type names to PATCH /profiles/edit-profile/
 * matching the mobile multipart payload format.
 */
import React, { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { FiArrowLeft, FiCheckCircle, FiCircle, FiSave, FiAlertTriangle } from 'react-icons/fi';
import { MdOutlineTune } from 'react-icons/md';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useAuth } from '../../../hooks/useAuth';
import { useGetUserTypes, useUpdateUserTypes } from '../../../api/settings';

// ── Type metadata (icons + descriptions + accent colours) ────────────────────

interface TypeMeta {
  icon: string;
  description: string;
  /** Tailwind bg class for selected state */
  selBg: string;
  /** Tailwind border class for selected state */
  selBorder: string;
  /** Tailwind text class for selected state */
  selText: string;
}

const TYPE_META: Record<string, TypeMeta> = {
  artist: {
    icon: '🎤',
    description: 'Solo musicians, bands, and performing artists',
    selBg: 'bg-purple-500/10',
    selBorder: 'border-purple-500/40',
    selText: 'text-purple-300',
  },
  dj: {
    icon: '🎧',
    description: 'DJs and electronic music performers',
    selBg: 'bg-blue-500/10',
    selBorder: 'border-blue-500/40',
    selText: 'text-blue-300',
  },
  venue: {
    icon: '🏟️',
    description: 'Music venues, clubs, and performance spaces',
    selBg: 'bg-emerald-500/10',
    selBorder: 'border-emerald-500/40',
    selText: 'text-emerald-300',
  },
  event_producer: {
    icon: '🎪',
    description: 'Event organizers and production companies',
    selBg: 'bg-orange-500/10',
    selBorder: 'border-orange-500/40',
    selText: 'text-orange-300',
  },
  crew: {
    icon: '🔧',
    description: 'Sound engineers, lighting crew, and event staff',
    selBg: 'bg-pink-500/10',
    selBorder: 'border-pink-500/40',
    selText: 'text-pink-300',
  },
  creator: {
    icon: '✨',
    description: 'Content creators and media personalities',
    selBg: 'bg-yellow-500/10',
    selBorder: 'border-yellow-500/40',
    selText: 'text-yellow-300',
  },
};

const DEFAULT_META: TypeMeta = {
  icon: '👤',
  description: 'A Musicade role',
  selBg: 'bg-blue-500/10',
  selBorder: 'border-blue-500/30',
  selText: 'text-blue-300',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const capitalize = (s: string) => {
  const CUSTOM_LABELS: Record<string, string> = {
    artist: 'Artist',
  };
  const normalized = s.toLowerCase();
  if (CUSTOM_LABELS[normalized]) return CUSTOM_LABELS[normalized];
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
};

const Spinner = ({ size = 16 }: { size?: number }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

const extractUserTypeName = (value: string | { name?: string }): string =>
  (typeof value === 'string' ? value : (value?.name ?? '')).trim();

// ── Component ─────────────────────────────────────────────────────────────────

export const ModifyUserTypePage: React.FC = () => {
  const { user } = useAuth();
  const { data: userTypes = [], isLoading } = useGetUserTypes();
  const updateMut = useUpdateUserTypes();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());

  // Hydrate from current user context
  useEffect(() => {
    if (!user?.userTypes || !Array.isArray(user.userTypes)) return;
    const names = new Set<string>(
      (user.userTypes as Array<string | { name?: string }>)
        .map((t) => extractUserTypeName(t))
        .filter(Boolean),
    );
    setSelected(new Set(names));
    setInitial(new Set(names));
  }, [user?.userTypes]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(name)) {
        s.delete(name);
      } else {
        s.add(name);
      }
      return s;
    });
  };

  const hasChanges = selected.size !== initial.size || [...selected].some((n) => !initial.has(n));

  const handleSave = async () => {
    if (!hasChanges || selected.size === 0) return;
    await updateMut.mutateAsync(Array.from(selected));
    setInitial(new Set(selected));
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-zinc-500 text-[12px] font-medium">
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <FiArrowLeft size={13} className="text-blue-500/80" />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-200">Modify User Type</span>
        </nav>

        {/* Page header */}
        <div className="flex items-start sm:items-center justify-between gap-4 mb-8 flex-col sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
              <MdOutlineTune size={20} className="text-orange-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-[22px] leading-tight">Modify User Type</h1>
              <p className="text-zinc-500 text-[13px]">
                Change your artist, venue, or producer role
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || selected.size === 0 || updateMut.isPending}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold transition-all shrink-0"
          >
            {updateMut.isPending ? <Spinner size={14} /> : <FiSave size={14} />}
            Save Changes
          </button>
        </div>

        {/* Info banner */}
        <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-4 mb-6">
          <p className="text-zinc-400 text-[13px] leading-relaxed">
            Select one or more roles that describe how you use Musicade. Your chosen types may
            unlock additional features — for example, payment setup is available to artists, DJs,
            and venues.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-60">
            <Spinner size={36} />
          </div>
        ) : (
          <>
            {/* Type cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {userTypes.map((type) => {
                const canonicalName = extractUserTypeName(type.name);
                const metaKey = canonicalName.toLowerCase();
                const meta = TYPE_META[metaKey] ?? DEFAULT_META;
                const isSelected = selected.has(canonicalName);

                return (
                  <button
                    key={type.id}
                    onClick={() => toggle(canonicalName)}
                    className={`relative text-left p-5 rounded-2xl border transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                      isSelected
                        ? `${meta.selBg} ${meta.selBorder}`
                        : 'bg-white/3 border-white/8 hover:bg-white/5 hover:border-white/12'
                    }`}
                  >
                    {/* Icon + checkbox row */}
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-2xl leading-none">{meta.icon}</span>
                      {isSelected ? (
                        <FiCheckCircle size={18} className="text-blue-400 shrink-0 mt-0.5" />
                      ) : (
                        <FiCircle
                          size={18}
                          className="text-zinc-700 shrink-0 mt-0.5 group-hover:text-zinc-500 transition-colors"
                        />
                      )}
                    </div>

                    {/* Name */}
                    <p
                      className={`font-semibold text-[14px] mb-1.5 ${isSelected ? meta.selText : 'text-white'}`}
                    >
                      {capitalize(canonicalName)}
                    </p>

                    {/* Description */}
                    <p className="text-zinc-500 text-[12px] leading-relaxed">{meta.description}</p>
                  </button>
                );
              })}
            </div>

            {userTypes.length === 0 && (
              <div className="text-center py-16 text-zinc-600">
                <p className="text-[14px]">No user types available. Please try again later.</p>
              </div>
            )}

            {/* Warning: nothing selected */}
            {selected.size === 0 && userTypes.length > 0 && (
              <div className="mt-5 flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl px-4 py-3">
                <FiAlertTriangle size={16} className="text-amber-400 shrink-0" />
                <p className="text-amber-400/90 text-[13px]">
                  Please select at least one user type before saving.
                </p>
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
